import { supabase } from '@/integrations/supabase/client';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

let sessionId: string | null = null;
let currentPath: string | null = null;
let trackingInterval: NodeJS.Timeout | null = null;

// Generate a unique session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session ID
const getSessionId = (): string => {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('website_session_id');
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('website_session_id', sessionId);
    }
  }
  return sessionId;
};

// Get location data from browser
const getCurrentLocation = (): Promise<LocationData | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// Get IP-based location (fallback)
const getIPLocation = async (): Promise<{ city?: string; country?: string } | null> => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) throw new Error('Failed to fetch IP location');
    
    const data = await response.json();
    return {
      city: data.city || undefined,
      country: data.country_name || undefined
    };
  } catch (error) {
    console.log('IP location fetch failed:', error);
    return null;
  }
};

// Track website access (for ALL visitors - anonymous and authenticated)
const trackAccess = async (path?: string): Promise<void> => {
  try {
    const currentUser = await supabase.auth.getUser();
    const sessionIdValue = getSessionId();
    const pagePath = path || window.location.pathname;
    
    // Skip if path hasn't changed
    if (currentPath === pagePath) {
      return;
    }
    
    currentPath = pagePath;
    
    // Get location data
    const locationData = await getCurrentLocation();
    const ipLocation = await getIPLocation();
    
    // Prepare access data (track all visitors, authenticated or not)
    const accessData = {
      user_id: currentUser.data.user?.id || null, // null for anonymous
      session_id: sessionIdValue,
      ip_address: null, // Browser can't access real IP
      latitude: locationData?.latitude || null,
      longitude: locationData?.longitude || null,
      accuracy: locationData?.accuracy || null,
      city: ipLocation?.city || null,
      country: ipLocation?.country || null,
      user_agent: navigator.userAgent.substring(0, 255), // Limit length
      page_path: pagePath,
      last_seen: new Date().toISOString()
    };

    // Upsert access record via secure RPC
    const { error: upsertError } = await supabase.rpc('upsert_website_access', {
      p_session_id: sessionIdValue,
      p_page_path: pagePath,
      p_user_id: currentUser.data.user?.id || null,
      p_ip: null,
      p_city: ipLocation?.city || null,
      p_country: ipLocation?.country || null,
      p_user_agent: navigator.userAgent.substring(0, 255),
      p_last_seen: new Date().toISOString(),
      p_lat: locationData?.latitude || null,
      p_lng: locationData?.longitude || null,
      p_accuracy: locationData?.accuracy || null,
    });

    if (upsertError) {
      console.log('Failed to track website access (RPC):', upsertError);
    } else {
      console.log('Successfully upserted website access for:', pagePath);
    }
  } catch (error) {
    console.log('Error tracking website access:', error);
  }
};

// Update last seen timestamp (for all visitors)
const updateLastSeen = async (): Promise<void> => {
  try {
    const sessionIdValue = getSessionId();
    
    const { error } = await supabase.rpc('upsert_website_access', {
      p_session_id: sessionIdValue,
      p_page_path: window.location.pathname,
      p_user_id: null,
      p_ip: null,
      p_city: null,
      p_country: null,
      p_user_agent: navigator.userAgent.substring(0, 255),
      p_last_seen: new Date().toISOString(),
      p_lat: null,
      p_lng: null,
      p_accuracy: null,
    });

    if (error) {
      console.log('Failed to update last seen:', error);
    }
  } catch (error) {
    console.log('Error updating last seen:', error);
  }
};

// Start tracking
export const startWebsiteAccessTracking = (): void => {
  // Track initial page load
  trackAccess();
  
  // Track page changes
  const handleRouteChange = () => {
    trackAccess();
  };
  
  // Listen for pushstate/popstate events (for SPA routing)
  window.addEventListener('popstate', handleRouteChange);
  
  // Override pushState and replaceState to catch route changes
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    setTimeout(handleRouteChange, 0);
  };
  
  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    setTimeout(handleRouteChange, 0);
  };
  
  // Update last seen every 30 seconds
  trackingInterval = setInterval(updateLastSeen, 30000);
  
  // Track when user becomes active/inactive
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      updateLastSeen();
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Track before page unload
  window.addEventListener('beforeunload', () => {
    updateLastSeen();
  });
};

// Stop tracking
export const stopWebsiteAccessTracking = (): void => {
  if (trackingInterval) {
    clearInterval(trackingInterval);
    trackingInterval = null;
  }
  
  // Remove event listeners
  window.removeEventListener('popstate', () => trackAccess());
  document.removeEventListener('visibilitychange', () => updateLastSeen());
  
  // Reset session
  sessionId = null;
  currentPath = null;
};
